import React from 'react';
import { T, MONO, radius, tint } from './theme';

let _uid = 0;
const uid = () => 'pg' + (++_uid);

// ── Sparkline (gradient fill under a smooth line) ──────────────
export function Sparkline({ data, color = T.green, width = 72, height = 28 }:
  { data: number[]; color?: string; width?: number; height?: number }) {
  const max = Math.max(...data), min = Math.min(...data), r = (max - min) || 1;
  const X = (i: number) => (i / (data.length - 1)) * width;
  const Y = (v: number) => height - ((v - min) / r) * (height - 5) - 3;
  const line = data.map((v, i) => (i ? 'L' : 'M') + X(i).toFixed(1) + ' ' + Y(v).toFixed(1)).join(' ');
  const fill = `${line} L ${width} ${height} L 0 ${height} Z`;
  const id = uid();
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block', overflow: 'visible' }}>
      <defs><linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity={0.26} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient></defs>
      <path d={fill} fill={`url(#${id})`} />
      <path d={line} fill="none" stroke={color} strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const Labels = ({ labels }: { labels: string[] }) => {
  // Dar alanda çok sayıda etiket taşmasın diye, 8'den fazlaysa aralıklı göster.
  const step = labels.length > 8 ? Math.ceil(labels.length / 6) : 1;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 9, padding: '0 2px' }}>
      {labels.map((l, i) => (
        (i % step === 0 || i === labels.length - 1) ? (
          <span key={i} style={{ fontSize: 10, color: T.textFaint, fontFamily: MONO, whiteSpace: 'nowrap' as const, flexShrink: 0 }}>{l}</span>
        ) : <span key={i} style={{ flexShrink: 0 }} />
      ))}
    </div>
  );
};

// ── Area chart (responsive width via preserveAspectRatio) ──────
export function AreaChart({ data, labels, color = T.gold, height = 132 }:
  { data: number[]; labels: string[]; color?: string; height?: number }) {
  const w = 600, pl = 6, pr = 6, pt = 12, pb = 4;
  const W = w - pl - pr, H = height - pt - pb;
  const max = Math.max(...data) * 1.12 || 1;
  const X = (i: number) => pl + (i / (data.length - 1)) * W;
  const Y = (v: number) => pt + H - (v / max) * H;
  const line = data.map((v, i) => (i ? 'L' : 'M') + X(i).toFixed(1) + ' ' + Y(v).toFixed(1)).join(' ');
  const fill = `${line} L ${X(data.length - 1).toFixed(1)} ${pt + H} L ${pl} ${pt + H} Z`;
  const id = uid();
  return (
    <div style={{ width: '100%' }}>
      <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" style={{ display: 'block' }}>
        <defs><linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.2} /><stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient></defs>
        {[0, 0.5, 1].map((f, k) => (
          <line key={k} x1={pl} y1={pt + H * (1 - f)} x2={pl + W} y2={pt + H * (1 - f)}
            stroke={T.border} strokeWidth={1} strokeDasharray="2 6" vectorEffect="non-scaling-stroke" />
        ))}
        <path d={fill} fill={`url(#${id})`} />
        <path d={line} fill="none" stroke={color} strokeWidth={2.25} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        <circle cx={X(data.length - 1)} cy={Y(data[data.length - 1])} r={3} fill={color} stroke={T.surface} strokeWidth={2} vectorEffect="non-scaling-stroke" />
      </svg>
      <Labels labels={labels} />
    </div>
  );
}

// ── Bar chart (last bar highlighted) ───────────────────────────
export function BarChart({ data, labels, color = T.gold, height = 132 }:
  { data: number[]; labels: string[]; color?: string; height?: number }) {
  const n = data.length, unit = 44, w = n * unit, pt = 12, pb = 4, H = height - pt - pb;
  const max = Math.max(...data) || 1;
  return (
    <div style={{ width: '100%' }}>
      <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" style={{ display: 'block' }}>
        {data.map((v, i) => {
          const bh = (v / max) * H, x = i * unit + 11, y = pt + H - bh, last = i === n - 1;
          return (
            <g key={i}>
              <rect x={x} y={pt} width={22} height={H} rx={6} fill={T.trackBg} />
              <rect x={x} y={y} width={22} height={bh} rx={6} fill={last ? color : tint(color, 40)} />
            </g>
          );
        })}
      </svg>
      <Labels labels={labels} />
    </div>
  );
}

// ── Chart type toggle (Area / Bar segmented control) ───────────
export function ChartToggle({ value, onChange }:
  { value: 'area' | 'bar'; onChange: (v: 'area' | 'bar') => void }) {
  const base: React.CSSProperties = { padding: '5px 12px', borderRadius: 7, border: 'none',
    fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all .15s' };
  const on: React.CSSProperties = { ...base, background: T.surface, color: T.text, boxShadow: '0 1px 2px rgba(20,22,43,.12)' };
  const off: React.CSSProperties = { ...base, background: 'transparent', color: T.textDim };
  return (
    <div style={{ display: 'flex', gap: 3, background: T.trackBg, border: `1px solid ${T.borderMid}`, borderRadius: 9, padding: 3 }}>
      <button style={value === 'area' ? on : off} onClick={() => onChange('area')}>Area</button>
      <button style={value === 'bar' ? on : off} onClick={() => onChange('bar')}>Bar</button>
    </div>
  );
}

// ── Donut (two-segment share ring) ─────────────────────────────
export function Donut({ gPct, mPct, size = 132, thickness = 18 }:
  { gPct: number; mPct: number; size?: number; thickness?: number }) {
  const R = (size - thickness) / 2, C = size / 2, circ = 2 * Math.PI * R, gap = 5;
  const gArc = (gPct / 100) * circ, mArc = (mPct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
      <circle cx={C} cy={C} r={R} fill="none" stroke={T.trackBg} strokeWidth={thickness} />
      <circle cx={C} cy={C} r={R} fill="none" stroke={T.gold} strokeWidth={thickness}
        strokeDasharray={`${(gArc - gap).toFixed(1)} ${(circ - gArc + gap).toFixed(1)}`} strokeLinecap="round" />
      <circle cx={C} cy={C} r={R} fill="none" stroke={T.purple} strokeWidth={thickness}
        strokeDasharray={`${(mArc - gap).toFixed(1)} ${(circ - mArc + gap).toFixed(1)}`}
        strokeDashoffset={`${-gArc.toFixed(1)}`} strokeLinecap="round" />
    </svg>
  );
}
